import { useMemo, useRef } from 'react';

import { flattenPages } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import type {
  RunOutcome,
  RunPositionMetrics,
} from './use-connector-run-history';
import { useConnectorRunHistory } from './use-connector-run-history';
import {
  computeTerminalMetrics,
  useResolvedConnectorStatuses,
} from './use-connector-terminal-metrics';
import { useListConnectorFeatures } from './use-list-connector-features';

export type ConnectorDashboardRowData = {
  metrics: {
    collected: number | undefined;
    changed: number | undefined;
    deleted: number | undefined;
    failedCount: number;
    hasData: boolean;
  };
  totalChanged: number;
  totalDeleted: number;
  runMetrics: RunPositionMetrics[];
  runs: RunOutcome[];
  featureSummary: { failing: number; total: number } | undefined;
  featureCounts: { enabled: number; disabled: number };
  needsAttention: boolean;
  isCardLoading: boolean;
  isLoadingRuns: boolean;
  isFeaturesPending: boolean;
};

/**
 * Fetches and derives all data needed to render a ConnectorRow card on the
 * dashboard. Encapsulates status resolution, feature loading, run history, and
 * all metric derivations.
 */
export function useConnectorDashboardRow(
  tenantId: UUID,
  connectorId: UUID
): ConnectorDashboardRowData {
  const {
    statuses,
    previousStatuses,
    capturedTerminal,
    isPending: isStatusesPending,
  } = useResolvedConnectorStatuses(tenantId, connectorId);

  const { data: featuresData, isPending: isFeaturesPending } =
    useListConnectorFeatures(tenantId, connectorId, { limit: 100 });

  const collectors = useMemo(
    () =>
      flattenPages(featuresData?.pages).filter((f) => f.schedulable !== false),
    [featuresData]
  );

  const collectorIds = useMemo(
    () => new Set(collectors.map((f) => f.featureId)),
    [collectors]
  );

  const collectorFeatureIds = useMemo(
    () => collectors.map((f) => f.featureId),
    [collectors]
  );

  const {
    runs: runHistory,
    featureRuns,
    needsAttention,
    runMetrics,
    isLoading: isLoadingRuns,
  } = useConnectorRunHistory(
    tenantId,
    connectorId,
    collectorFeatureIds,
    statuses
  );

  const featureSummary = useMemo(() => {
    if (featureRuns.size === 0) return undefined;
    const failing = Array.from(featureRuns.values()).filter(
      (h) => h.needsAttention
    ).length;
    return { failing, total: featureRuns.size };
  }, [featureRuns]);

  const featureCounts = useMemo(() => {
    const enabled = collectors.filter((f) => f.status === true).length;
    const disabled = collectors.filter((f) => f.status === false).length;
    return { enabled, disabled };
  }, [collectors]);

  const computedMetrics = useMemo(() => {
    const hasCollectors = (Object.keys(statuses) as UUID[]).some((id) =>
      collectorIds.has(id)
    );
    if (!hasCollectors) {
      return {
        collected: undefined as number | undefined,
        changed: undefined as number | undefined,
        deleted: undefined as number | undefined,
        failedCount: 0,
        hasData: false,
      };
    }

    const raw = computeTerminalMetrics(
      statuses,
      previousStatuses,
      capturedTerminal,
      collectorIds
    );
    return {
      collected: raw.hasData ? raw.collected : undefined,
      changed: raw.hasData ? raw.changed : undefined,
      deleted: raw.hasData ? raw.deleted : undefined,
      failedCount: raw.failedCount,
      hasData: raw.hasData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses, previousStatuses, collectorIds, capturedTerminal]);

  // Keep last known good metrics to prevent flash-to-empty while previous
  // invocation statuses are being resolved after a run transition.
  const lastKnownMetrics = useRef(computedMetrics);
  if (computedMetrics.hasData) {
    lastKnownMetrics.current = computedMetrics;
  }
  const metrics = computedMetrics.hasData
    ? computedMetrics
    : lastKnownMetrics.current;

  const totalChanged = useMemo(
    () => runMetrics.reduce((sum, m) => sum + m.changed, 0),
    [runMetrics]
  );
  const totalDeleted = useMemo(
    () => runMetrics.reduce((sum, m) => sum + m.deleted, 0),
    [runMetrics]
  );

  return {
    metrics,
    totalChanged,
    totalDeleted,
    runMetrics,
    runs: runHistory,
    featureSummary,
    featureCounts,
    needsAttention,
    // The breakdown is derived from both invocation statuses and features, so
    // the card is still "loading" while either source is settling. Using OR
    // (not AND) keeps the placeholder in place until both have arrived —
    // otherwise the skeleton clears the moment one resolves and the stats pop
    // in afterwards, causing a flash/layout shift.
    isCardLoading: isStatusesPending || isFeaturesPending,
    // Run history can't begin loading until features resolve (the per-feature
    // invocation queries are keyed off the feature ids). While features are
    // pending the run-history hook reports isLoading=false because it has no
    // queries to run yet — so we OR in isFeaturesPending here, otherwise the
    // chart shows its empty state until listConnectorInvocationStatuses fires
    // and only then flips to a skeleton.
    isLoadingRuns: isFeaturesPending || isLoadingRuns,
    isFeaturesPending,
  };
}
